import React, { useCallback, useEffect, useRef, useState } from 'react'
import Datetime from 'react-datetime'
import moment, { Moment } from 'moment'
import 'moment/locale/ru'
import 'react-datetime/css/react-datetime.css'
import styles from './DatePicker.module.css'
import classNames from 'classnames'

moment.locale('ru')

const PICKER_GAP = 8
const PICKER_FALLBACK_HEIGHT = 300

function findScrollableContainer(element: HTMLElement): HTMLElement {
  let parent = element.parentElement

  while (parent && parent !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(parent)

    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return parent
    }

    parent = parent.parentElement
  }

  return document.documentElement
}

/** Видимая область scroll-контейнера с учётом viewport */
function getPlacementContainerBounds(anchor: HTMLElement): {
  top: number
  bottom: number
} {
  const scrollContainer = findScrollableContainer(anchor)
  const { top, bottom } = scrollContainer.getBoundingClientRect()

  return {
    top: Math.max(top, 0),
    bottom: Math.min(bottom, window.innerHeight),
  }
}

interface DateTimePickerProps {
  id: string
  selectedDate: string | Moment
  setSelectedDate: (date: string) => void
  placeholder?: string
  addPosition?: boolean
  isValidateDate?: boolean
  timeFormat?: boolean
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  id,
  selectedDate,
  setSelectedDate,
  placeholder,
  addPosition = true,
  isValidateDate = true,
  timeFormat = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [opensUp, setOpensUp] = useState(false)

  const updatePickerPlacement = useCallback(() => {
    if (!addPosition) return

    const container = containerRef.current
    if (!container) return

    const inputRect = container.getBoundingClientRect()
    const picker = container.querySelector('.rdtPicker') as HTMLElement | null
    const pickerHeight = picker?.offsetHeight ?? PICKER_FALLBACK_HEIGHT
    const { top: containerTop, bottom: containerBottom } =
      getPlacementContainerBounds(container)

    const spaceBelow = containerBottom - inputRect.bottom - PICKER_GAP
    const spaceAbove = inputRect.top - containerTop - PICKER_GAP

    if (spaceBelow >= pickerHeight) {
      setOpensUp(false)
      return
    }
    if (spaceAbove >= pickerHeight) {
      setOpensUp(true)
      return
    }
    setOpensUp(spaceAbove > spaceBelow)
  }, [addPosition])

  const handleOpen = useCallback(() => {
    requestAnimationFrame(() => {
      updatePickerPlacement()
      requestAnimationFrame(updatePickerPlacement)
    })
  }, [updatePickerPlacement])

  /** 00:00 → 00:01: иначе день «уплывает», а react-datetime часто шлёт полночь повторно */
  const withDefaultTime = (date: Moment) => {
    if (!timeFormat) return date
    if (date.hours() === 0 && date.minutes() === 0) {
      return date.clone().hours(0).minutes(1).seconds(0).milliseconds(0)
    }
    return date
  }

  useEffect(() => {
    if (!timeFormat || !selectedDate) return

    const parsed = moment.isMoment(selectedDate)
      ? selectedDate
      : moment(selectedDate, 'YYYY-MM-DD HH:mm', true)

    if (!parsed.isValid()) return
    if (parsed.hours() !== 0 || parsed.minutes() !== 0) return

    setSelectedDate(
      parsed.clone().hours(0).minutes(1).seconds(0).milliseconds(0).format(
        'YYYY-MM-DD HH:mm'
      )
    )
  }, [selectedDate, setSelectedDate, timeFormat])

  const handleDateChange = (date: Moment | string) => {
    if (moment.isMoment(date) && date.isValid()) {
      if (isValidateDate && date.isAfter(moment(), 'day')) {
        setSelectedDate('')
      } else {
        setSelectedDate(withDefaultTime(date).format('YYYY-MM-DD HH:mm'))
      }
    } else if (
      typeof date === 'string' &&
      moment(date, 'YYYY-MM-DD HH:mm', true).isValid()
    ) {
      const inputDate = withDefaultTime(
        moment(date, 'YYYY-MM-DD HH:mm', true)
      )
      if (isValidateDate && inputDate.isAfter(moment(), 'day')) {
        setSelectedDate('')
      } else {
        setSelectedDate(inputDate.format('YYYY-MM-DD HH:mm'))
      }
    } else {
      setSelectedDate('')
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputDate = moment(event.target.value, 'YYYY-MM-DD HH:mm', true)

    if (!isValidateDate) {
      return
    }
    if (inputDate.isValid() && inputDate.isAfter(moment(), 'day')) {
      setSelectedDate('')
    }
  }

  const isValidDate = (current: Moment) => {
    return current.isSameOrBefore(moment(), 'day')
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <Datetime
        inputProps={{
          id,
          placeholder: placeholder || undefined,
          autoComplete: 'off',
          onBlur: handleBlur,
          onFocus: addPosition ? handleOpen : undefined,
        }}
        value={
          selectedDate
            ? withDefaultTime(moment(selectedDate, 'YYYY-MM-DD HH:mm'))
            : ''
        }
        onChange={handleDateChange}
        onOpen={addPosition ? handleOpen : undefined}
        className={classNames(
          styles.calendar,
          addPosition && styles.addPosition,
          addPosition && (opensUp ? styles.openUp : styles.openDown)
        )}
        locale="ru"
        timeFormat={timeFormat}
        isValidDate={isValidateDate ? isValidDate : undefined}
      />
      <div className={styles.calendarImage} />
    </div>
  )
}

export default DateTimePicker
